<template>
  <div class="flex-grow flex flex-col overflow-hidden p-6 relative h-full max-h-full">
    <!-- Üst Kısım - Ekleme ve Dışa Aktarma Butonları -->
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-medium text-gray-700">
        {{ t('settings.components.configuration.components.categoryModal.list.customCategories') }} </h3>
      <div class="flex space-x-2">
        <!-- Ekleme Butonu -->
        <VButton @click="$emit('add-new')" :button-text="t('settings.components.configuration.components.categoryModal.list.addNewButton')" icon="add_circle" :show-icon="true" class="hover:shadow-md transition-all duration-300 mr-2" :is-disabled="isLoading"/>
        <!-- Prompt Yönetim Butonu - Kategori yoksa devre dışı -->
        <VButton 
          @click="$emit('open-prompt-manager')" 
          button-text="Prompt Yöneticisi" 
          icon="smart_toy" 
          :show-icon="true" 
          class="hover:shadow-md transition-all duration-300" 
          :is-disabled="isLoading || !categories.length" 
          :title="!categories.length ? 'Önce bir kategori eklemelisiniz' : ''"
          variant="secondary"/>
      </div>
    </div>

    <!-- Kategori Listesi -->
    <div class="flex-grow overflow-auto thin-scrollbar relative h-full">
      <!-- Loading State -->
      <div v-if="isLoading" class="absolute inset-0 bg-white/80 backdrop-blur-sm flex justify-center items-center">
        <div class="flex flex-col items-center gap-2">
          <span class="animate-spin material-symbols-outlined text-3xl text-primary">progress_activity</span>
          <span class="text-sm text-gray-600">{{ t('settings.components.configuration.components.categoryModal.common.loading') }}</span>
        </div>
      </div>

      <!-- Boş Durum - Kategori yok -->
      <div v-else-if="!categories.length" class="h-full flex flex-col items-center justify-center text-center p-8">
        <span class="material-symbols-outlined text-5xl text-gray-400 mb-4">category</span>
        <p class="text-lg font-medium text-gray-600 mb-2">{{ t('settings.components.configuration.components.categoryModal.list.emptyState.title') }}</p>
        <p class="text-sm text-gray-500 mb-6">{{ t('settings.components.configuration.components.categoryModal.list.emptyState.description') }}</p>
        
        <!-- Add button in empty state for better UX -->
        <VButton 
          @click="$emit('add-new')" 
          :button-text="t('settings.components.configuration.components.categoryModal.list.addNewButton')" 
          icon="add_circle" 
          :show-icon="true" 
          class="mt-2" 
          size="lg"/>
        
        <!-- Açıklama mesajı - Prompt için kategori gerektiğini belirt -->
        <div class="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-md">
          <div class="flex items-start gap-2">
            <span class="material-symbols-outlined text-amber-600 mt-0.5">info</span>
            <div>
              <h4 class="font-medium text-amber-800 mb-1">Prompt Yönetimi için kategori gereklidir</h4>
              <p class="text-sm text-amber-700">Prompt yöneticisini kullanabilmek için önce en az bir kategori oluşturmalısınız.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Kategoriler Listesi -->
      <div v-else class="space-y-2">
        <CategoryItem v-for="category in categories" :key="category.id" :category="category" :sub-categories="subCategories[category.id]" :is-expanded="expandedCategories.includes(category.id)" :is-loading-sub="loadingSubCategories === category.id" :is-deleting="isDeleting && selectedCategory?.id === category.id" :expanded-categories="expandedCategories" :loading-sub-categories="loadingSubCategories" :sub-categories-map="subCategories" @toggle="handleToggle" @delete="handleDelete" @open-prompt="handleOpenPrompt" class="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300"/>
      </div>
    </div>

    <!-- Silme Onay Modal -->
    <DeleteConfirmationModal :show-delete-confirmation="showDeleteConfirmation" :category="selectedCategory" :is-deleting="isDeleting" @confirm="confirmDelete" @cancel="cancelDelete"/>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAssetStore } from '@/stores/asset'
import CategoryItem from './CategoryItem.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import { deleteCategory } from '../../services/management'
import DeleteConfirmationModal from './DeleteConfirmationModal.vue'

const { t } = useI18n()

const props = defineProps({
  categories: {
    type: Array,
    required: true,
  },
  subCategories: {
    type: Object,
    required: true,
  },
  isLoading: Boolean,
  expandedCategories: {
    type: Array,
    required: true,
  },
  loadingSubCategories: [String, Number, null],
})

const emit = defineEmits(['add-new', 'toggle-category', 'category-deleted', 'open-prompt-manager', 'open-category-prompt'])

const assetStore = useAssetStore()

// Delete related states
const showDeleteConfirmation = ref(false)
const selectedCategory = ref(null)
const isDeleting = ref(false)

// Handle opening prompt for specific category
const handleOpenPrompt = (category) => {
  emit('open-category-prompt', category)
}

const handleToggle = (data) => {
  emit('toggle-category', data)
}

// Delete handlers
const handleDelete = (category) => {
  selectedCategory.value = category
  showDeleteConfirmation.value = true
}

const cancelDelete = () => {
  showDeleteConfirmation.value = false
  selectedCategory.value = null
}

const confirmDelete = async () => {
  if (!selectedCategory.value?.id) {
    return
  }

  isDeleting.value = true

  const result = await deleteCategory(
      selectedCategory.value,
      assetStore.assetId,
      t,
  )

  if (result.success) {
    emit('category-deleted', result.deletedInfo)
    showDeleteConfirmation.value = false
    selectedCategory.value = null
  }

  isDeleting.value = false
}
</script>